import { isFunction } from 'lodash';
import { useRouter } from 'next/router';
import { useRef } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';

import { RadioTabs } from '@/components/inputs/radio-tabs';
import { Toggle } from '@/components/inputs/toggle';
import { LocationAutocomplete } from '@/components/location-autocomplete';
import { ComboBox } from '@/components/ui/inputs/combobox';
import { Select } from '@/components/ui/inputs/select';
import { usePorts } from '@/hooks/use-ports';
import { useRouterQuery } from '@/hooks/use-router-query';
import { useUpdateElementTotalHeightCssVar } from '@/hooks/use-update-responsive-css-var';
import { useUserSettings } from '@/hooks/use-user-settings';
import { AIRPORT, DISTANCE_UNITS, MAP_LINE_STYLE_DASHED, MODE_AIR, PORTS_INSIGHTS_URL, SEAPORT } from '@/lib/constants';
import {
  globalPortsReset,
  locationAutocompleteValueState,
  portsFiltersCarriersState,
  portsFiltersState,
  portsMapLocationsState,
  portsSearchParamsState,
  portsTransportModesText,
  searchRadiusState,
  selectedPortsState,
} from '@/state/ports';
import { LocationInterface, OptionItem, PortsFiltersInterface } from '@/types';
import { PlaceInterface, SegmentModeType } from '@/types/api-types';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

import { mapCarriersToOptions } from './utils';

const SearchFields = () => {
  const router = useRouter();
  const { addUrlQueryParam } = useRouterQuery();
  const { performPortsSearch } = usePorts();
  const { distanceUnits, updateUserSettings, isLoading } = useUserSettings();

  const setMapLocations = useSetRecoilState(portsMapLocationsState);
  const [searchParams, setSearchParams] = useRecoilState(portsSearchParamsState);
  const selectedPorts = useRecoilValue(selectedPortsState);
  const [portsFilters, setPortsFilters] = useRecoilState(portsFiltersState);
  const resetPortsFilters = useResetRecoilState(portsFiltersState);
  const [locationAutocompleteValue, setLocationAutocompleteValue] = useRecoilState(locationAutocompleteValueState);
  const [searchRadius, setSearchRadius] = useRecoilState(searchRadiusState);
  const resetPorts = useResetRecoilState(globalPortsReset);
  const filtersCarriers = useRecoilValue(portsFiltersCarriersState);
  const { transportMode, carrierComboboxLabel } = useRecoilValue(portsTransportModesText);

  const groupedCarriers = mapCarriersToOptions(filtersCarriers);

  const elementRef = useRef(null);
  useUpdateElementTotalHeightCssVar(elementRef, '--port-insights-search-bar-height');

  const updateMapLocation = (place: LocationInterface | null = null) => {
    if (!place) return;
    setMapLocations({
      id: 'searchQuery',
      paths: [
        {
          id: 'path',
          style: MAP_LINE_STYLE_DASHED,
          locations: [place],
        },
      ],
      markers: [place],
    });
  };

  // When an autocomplete option is selected
  const handleSelectLocation = (place?: PlaceInterface) => {
    if (!place) {
      return setSearchParams((prev) => ({ ...prev, origin: null }));
    }

    resetPorts();
    setSearchParams((prev) => ({ ...prev, origin: place }));
    updateMapLocation(place);
    setLocationAutocompleteValue(place.name);
    addUrlQueryParam('place', place.name);

    // Analytics Event
    trackUser.event('Select Autocomplete Location', { place: place.name });

    return null;
  };

  const handleSelectTransportType = (value: PortsFiltersInterface['transportType']) => {
    setPortsFilters((prev) => ({ ...prev, transportType: value, carrier: undefined }));

    // If there are carrier filters, we have a filtered list from the API so
    // we have to go back to the API to get all the ports again
    if (portsFilters.carrier && isFunction(performPortsSearch)) {
      performPortsSearch();
    }

    router.replace(PORTS_INSIGHTS_URL, undefined, { shallow: true });

    // Analytics Event
    trackUser.event('Change Transport Filter', { value });
  };

  const handleSelectCarriers = (item: OptionItem) => {
    // If the item's value is 'all', then we want to remove all carriers
    if (item.value === 'all') {
      setPortsFilters((prev) => {
        return {
          ...prev,
          carrier: undefined,
        };
      });

      // Go and get the unfiltered ports from the API
      if (isFunction(performPortsSearch)) {
        performPortsSearch();
      }

      return;
    }

    if (isFunction(performPortsSearch)) {
      performPortsSearch({
        carrier: item.value,
      });
    }

    // Set the selected carrier
    setPortsFilters((prev) => {
      return {
        ...prev,
        carrier: item,
      };
    });

    // Analytics Event
    trackUser.event('Change Carriers Filter', { value: item.value });
  };

  const handleChangeSearchRadius = (value: OptionItem) => {
    setSearchRadius(value.value);
  };

  const handleChangeDistanceUnits = async (value: boolean) => {
    const booleanToValue = () => (value ? DISTANCE_UNITS.KM : DISTANCE_UNITS.MI);

    await updateUserSettings({
      distanceUnits: {
        value: booleanToValue(),
      },
    });

    // Analytics Event
    trackUser.event('Change Distance Units', { value: booleanToValue() });
  };

  const handleReset = () => {
    setSearchParams((prev) => ({ ...prev, origin: null }));
    resetPorts();
    resetPortsFilters();
    setLocationAutocompleteValue('');

    if (isFunction(performPortsSearch)) {
      performPortsSearch();
    }

    router.replace(PORTS_INSIGHTS_URL, undefined, { shallow: true });

    // Analytics Event
    trackUser.event('Reset Port Insights Filters');
  };

  const {
    base,
    filters,
    resetButton,
    resetButtonContainer,
    toggleContainer,
    select,
    filtersContainer,
    filtersInner,
    locationAutocomplete,
  } = styles();

  return (
    <div className={base()} ref={elementRef}>
      <div className="grid w-full max-w-[500px] grid-cols-[1fr,auto]">
        <LocationAutocomplete
          name="origin"
          placeholder="City, Port or Address"
          label="Find ports near"
          onSelect={handleSelectLocation}
          defaultValue={locationAutocompleteValue}
          className={locationAutocomplete()}
        />
        <Select
          id="PortInsights:Radius"
          placeholder="Search radius"
          label="Within"
          selected={searchRadius}
          onSelect={handleChangeSearchRadius}
          options={[
            {
              title: 'Options',
              items: [100, 200, 300, 400, 1000, 1600].map((distance) => ({
                value: distance.toString(),
                label: `${distance} ${distanceUnits?.value}`,
              })),
            },
          ]}
          disabled={!!selectedPorts?.origin}
          className={select()}
        />
      </div>
      <div className={filtersContainer()}>
        <div className={filters()}>
          <div>
            <div className={filtersInner()}>
              <RadioTabs
                name="transportType"
                label="Transport type"
                options={[
                  { value: 'all', label: 'All' },
                  { value: AIRPORT, label: 'Airports' },
                  { value: SEAPORT, label: 'Seaports' },
                ]}
                onChange={handleSelectTransportType}
                selected={portsFilters.transportType}
                disabled={!!selectedPorts?.origin}
              />

              <div className="max-md:hidden">
                <ComboBox
                  id="carriers"
                  name="carriers"
                  label="Carriers"
                  placeholder={!transportMode ? 'Select a transport mode' : `All ${carrierComboboxLabel}`}
                  options={[
                    {
                      title: '',
                      items: [{ value: 'all', label: `All ${carrierComboboxLabel}`, type: MODE_AIR }],
                    },
                    {
                      title: '',
                      items: (transportMode && groupedCarriers[transportMode as SegmentModeType]) || [],
                    },
                  ]}
                  onSelect={handleSelectCarriers}
                  selected={portsFilters.carrier?.value}
                  disabled={!!selectedPorts?.origin || !transportMode}
                  className="max-w-56"
                />
              </div>
            </div>
          </div>
        </div>
        {(selectedPorts?.origin || searchParams.origin || portsFilters.carrier) && (
          <div className={resetButtonContainer()}>
            <button onClick={handleReset} className={resetButton()} type="button">
              Reset
            </button>
          </div>
        )}
        <div className={toggleContainer()}>
          <Toggle
            name="toggle-distance-units"
            legend="Distance units"
            offLabel="miles"
            onLabel="km"
            onChange={handleChangeDistanceUnits}
            on={distanceUnits?.value === DISTANCE_UNITS.KM}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative flex flex-col items-start border-b border-solid border-grey-300 bg-grey-100 px-4 py-3 lg:flex-row lg:items-center',
    filtersContainer: 'flex w-full flex-1 items-end',
    filters: 'mt-3 flex items-end justify-center md:mt-4 lg:ml-4 lg:mt-0',
    filtersInner: 'grid items-start justify-start gap-x-2 max-md:gap-y-2.5 sm:grid-cols-2 md:grid-cols-[auto,1fr]',
    resetButton:
      'mb-3 border-b border-solid border-blue-100 border-transparent bg-transparent text-sm font-semibold text-blue-500 hover:text-blue-500 hover:underline',
    resetButtonContainer: 'ml-4 flex items-end max-md:hidden',
    toggleContainer: 'ml-auto flex items-center pb-2 pl-2',
    select: '-ml-px w-[118px] [&_button]:rounded-l-none',
    heading: 'mb-xs ml-xs text-grey-600',
    locationAutocomplete: 'w-full [&_input]:w-full [&_input]:min-w-none [&_input]:!rounded-r-none',
  },
});

export { SearchFields };
